// Single source of truth for process-map edges.
//
// The renderer used to decide each edge's label / kind / colour at draw time
// with ~18 heuristic branches (keyword matching, cross-lane guessing, yes/no
// assumptions). That left multi-way decisions (e.g. tier routing) and some
// decision branches unlabeled. The map data already carries a clean, fully
// labeled edge list in `elements.sequence_flows`, so we derive every edge from
// that ONCE here. The renderer then only routes lines and prints these values.

export type EdgeKind = 'sequence' | 'decision' | 'default' | 'loop';

export interface MapEdge {
  id: string;
  sourceId: string;        // resolved stable step id
  targetId: string;        // resolved stable step id
  label: string;           // condition text verbatim ('' for plain sequence)
  kind: EdgeKind;
  isDefault: boolean;
}

interface RawFlow {
  id?: string;
  source_step: string;
  target_step: string;
  condition?: string | null;
  condition_expression?: string | null;
  is_default?: boolean;
}

interface StepLike {
  id: string;
  step_number?: string | number;
  name?: string;
  type?: string;
  is_decision?: boolean;
  action_type?: string;
  bpmn_type?: string;
  position?: { x: number; y: number };
}

const norm = (ref: unknown): string => {
  if (ref == null) return '';
  return String(ref).replace(/^Step\s+/i, '').trim();
};

// Resolve a sequence_flow reference (step_number like "7" / "7A", an id, or a
// "Step N" string) to a concrete step's stable id. One resolver, used for both
// source and target, so references can never be interpreted two different ways.
function resolveStepId(ref: string, steps: StepLike[], byNumber: Map<string, StepLike>, byId: Map<string, StepLike>): string | null {
  if (!ref) return null;
  const r = norm(ref);
  // 1) exact step_number (covers "7", "7A", and named like "End-Declined-UW")
  if (byNumber.has(r)) return byNumber.get(r)!.id;
  // 2) exact id (with or without an L\d+_ prefix)
  if (byId.has(ref)) return byId.get(ref)!.id;
  const stripped = ref.replace(/^L\d+_/, '');
  if (byId.has(stripped)) return byId.get(stripped)!.id;
  // 3) name pattern "Step <num> -" / "Step <num>:"
  const byName = steps.find(s =>
    s.name?.includes(`Step ${r} -`) || s.name?.includes(`Step ${r}:`),
  );
  if (byName) return byName.id;
  // 4) last resort: a step whose id contains the stripped ref
  const byPartialId = steps.find(s => s.id === stripped || s.id.includes(stripped));
  return byPartialId ? byPartialId.id : null;
}

/**
 * Build the full edge list from the map's sequence_flows.
 *
 * Every edge's meaning (label, kind, default-ness) comes straight from the data
 * — no render-time inference. A decision with N outgoing flows produces N edges,
 * each carrying its own condition text; binary yes/no is just the N=2 case.
 */
export function buildEdges(steps: StepLike[], sequenceFlows: RawFlow[] | undefined): MapEdge[] {
  if (!Array.isArray(steps) || steps.length === 0) return [];
  if (!Array.isArray(sequenceFlows) || sequenceFlows.length === 0) return [];

  const byNumber = new Map<string, StepLike>();
  const byId = new Map<string, StepLike>();
  for (const s of steps) {
    byId.set(s.id, s);
    if (s.step_number != null) byNumber.set(norm(s.step_number), s);
  }

  const edges: MapEdge[] = [];
  const seen = new Set<string>();

  for (const f of sequenceFlows) {
    const sourceId = resolveStepId(f.source_step, steps, byNumber, byId);
    const targetId = resolveStepId(f.target_step, steps, byNumber, byId);
    if (!sourceId || !targetId) continue;

    const dedupeKey = `${sourceId}->${targetId}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    const source = byId.get(sourceId);
    const sourcePos = source?.position?.x ?? 0;
    const targetPos = byId.get(targetId)?.position?.x ?? 0;
    const isBackward = targetPos < sourcePos;
    const label = (f.condition || '').trim();

    // Kind is decided by the DATA, not by geometry or step-name keywords:
    //  - a flow with condition text is a labeled decision branch
    //    (is_default text == the fall-through branch, e.g. "Tier 1, 2, or 3")
    //  - a flow with no condition is a plain sequence / parallel-track link,
    //    which legitimately carries no label (even from a decision/split source)
    //  - a backward target is a loop
    let kind: EdgeKind;
    if (label) kind = f.is_default ? 'default' : 'decision';
    else if (isBackward) kind = 'loop';
    else kind = 'sequence';

    edges.push({
      id: f.id || `${sourceId}-${targetId}`,
      sourceId,
      targetId,
      label, // verbatim; '' for sequence/track links
      kind,
      isDefault: !!f.is_default,
    });
  }

  return edges;
}

// Visual style for an edge — derived ONLY from its kind, in one place.
export function edgeStyle(kind: EdgeKind): { color: string; labelColor: string; dash: string; arrow: string } {
  switch (kind) {
    case 'decision':
      return { color: '#6B7280', labelColor: '#10B981', dash: 'none', arrow: 'url(#decision-yes-arrowhead)' };
    case 'default':
      return { color: '#6B7280', labelColor: '#EF4444', dash: '5,5', arrow: 'url(#decision-no-arrowhead)' };
    case 'loop':
      return { color: '#6B7280', labelColor: '#EF4444', dash: '5,5', arrow: 'url(#decision-no-arrowhead)' };
    case 'sequence':
    default:
      return { color: '#6B7280', labelColor: '#1F2937', dash: 'none', arrow: 'url(#arrowhead)' };
  }
}
